import { useContext, useEffect, useRef, useState } from 'react'
import { FontIcon, Stack, TextField } from '@fluentui/react'
import { SendRegular } from '@fluentui/react-icons'

import Send from '../../assets/Send.svg'

import styles from './QuestionInput.module.css'
import { ChatMessage } from '../../api'
import { AppStateContext } from '../../state/AppProvider'
import { resizeImage } from '../../utils/resizeImage'
import { RxCross2 } from 'react-icons/rx'
import { BiSolidImageAdd, BiSolidSend } from 'react-icons/bi'
import { BsFileEarmarkImage } from 'react-icons/bs'
import { FaRegImage } from 'react-icons/fa6'
import { LuImagePlus } from 'react-icons/lu'

interface Props {
  onSend: (question: ChatMessage['content'], id?: string) => void
  disabled: boolean
  placeholder?: string
  clearOnSend?: boolean
  conversationId?: string
  textareaRef: React.RefObject<HTMLTextAreaElement>
  setPaddingBottom: any
}

export const QuestionInput = ({
  onSend,
  disabled,
  placeholder,
  clearOnSend,
  conversationId,
  textareaRef,
  setPaddingBottom
}: Props) => {
  const [question, setQuestion] = useState<string>('')
  const [base64Image, setBase64Image] = useState<string | null>(null)

  const appStateContext = useContext(AppStateContext)
  const OYD_ENABLED = appStateContext?.state.frontendSettings?.oyd_enabled || false

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    if (file) {
      await convertToBase64(file)
    }
  }

  const convertToBase64 = async (file: Blob) => {
    try {
      const resizedBase64 = await resizeImage(file, 800, 800)
      setBase64Image(resizedBase64)
    } catch (error) {
      console.error('Error:', error)
    }
  }

  const sendQuestion = () => {
    if (disabled || !question.trim()) {
      return
    }

    const questionTest: ChatMessage['content'] = base64Image
      ? [
          { type: 'text', text: question },
          { type: 'image_url', image_url: { url: base64Image } }
        ]
      : question.toString()

    if (conversationId && questionTest !== undefined) {
      onSend(questionTest, conversationId)
      setBase64Image(null)
    } else {
      onSend(questionTest)
      setBase64Image(null)
    }

    if (clearOnSend) {
      setQuestion('')
    }
  }

  const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
    if (ev.key === 'Enter' && !ev.shiftKey && !(ev.nativeEvent?.isComposing === true)) {
      ev.preventDefault()
      sendQuestion()
    }
  }

  const onQuestionChange = (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
    setQuestion(newValue || '')
  }

  const sendQuestionDisabled = disabled || !question.trim()

  console.log('question', question)

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const textarea = textareaRef.current
    if (textarea) {
      // Reset height to auto to correctly calculate scrollHeight
      textarea.style.height = 'auto'

      // Calculate new height but cap it at 504px
      const scrollHeight = textarea.scrollHeight
      const maxHeight = 120
      const newHeight = scrollHeight > maxHeight ? maxHeight : scrollHeight
      textarea.style.height = `${newHeight}px`

      // Define minimum textarea height (56px) and base padding (160px)
      const minTextareaHeight = 56
      const basePadding = 160
      const maxTextareaHeight = 120

      // Calculate new padding-bottom
      const additionalHeight = newHeight - minTextareaHeight
      const newPaddingBottom =
        newHeight > minTextareaHeight
          ? Math.min(basePadding + additionalHeight, basePadding + (maxTextareaHeight - minTextareaHeight))
          : basePadding

      setPaddingBottom(newPaddingBottom)
    }

    // Update the question state
    setQuestion(event.target.value)
  }

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }, [question])

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex items-center gap-3">
        {base64Image && (
          <div className="relative">
            <span
              onClick={() => setBase64Image(null)}
              className="absolute -right-1 -top-1 bg-white rounded-full text-black p-0.5 cursor-pointer">
              <RxCross2 />
            </span>
            <img className="w-16 h-16 rounded" src={base64Image} alt="Uploaded Preview" />
          </div>
        )}
      </div>
      <div className="flex flex-row min-h-16 md:max-h-[210px] w-full bg-background border-2 border-secondary md:rounded-2xl relative ">
        <textarea
          placeholder={placeholder}
          ref={textareaRef}
          className="scrollbar-custom w-full leading-7 mt-1 mb-12 mx-3 min-h-10 max-h-36 bg-transparent text-black flex-1 resize-none border-none focus:outline-none ml-3 mr-16 md:mr-16"
          value={question}
          onChange={handleChange}
          onKeyDown={onEnterPress}
        />
        {!OYD_ENABLED && (
          <div className="absolute left-0 px-4 bottom-4 rounded-lg flex items-end justify-between w-full gap-5">
            <div className="flex justify-center items-center">
              <input
                type="file"
                id="fileInput"
                onChange={event => handleImageUpload(event)}
                accept="image/*"
                className={styles.fileInput}
              />
              <label
                htmlFor="fileInput"
                className="inline-block rounded-[5px] cursor-pointer text-center text-[14px]"
                aria-label="Upload Image">
                <LuImagePlus className={'text-xl text-white hover:text-[#467FD1]'} aria-label="Upload Image" />
              </label>
            </div>
            <button
              className={`primary-btn hover:bg-[#e1b438 w-9 h-9 rounded-full ${sendQuestionDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer opacity-100'} `}
              role="button"
              disabled={sendQuestionDisabled}
              tabIndex={0}
              aria-label="Ask question button"
              onClick={sendQuestion}
              onKeyDown={e => (e.key === 'Enter' || e.key === ' ' ? sendQuestion() : null)}>
              <BiSolidSend className={`text-white text-xl`} />
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
